import { useEffect, useState } from 'react';
import './LandingPage.css';
import {
  getUnitSummary,
  getUnitContributions,
  createContribution,
  getContributionHistory,
  updateContribution,
  deleteContribution,
} from '../api/client';
import DashboardSummary from './DashboardSummary';
import ContributionsTab from './ContributionsTab';

export default function UnitLanding({ session, onLogout }) {
  const { user, token } = session;
  const [activeTab, setActiveTab] = useState('dashboard');

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState('');

  const [contribData, setContribData] = useState(null);
  const [contribLoading, setContribLoading] = useState(false);
  const [contribError, setContribError] = useState('');
  const [contribPage, setContribPage] = useState(1);

  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [historyPage, setHistoryPage] = useState(1);

  const loadSummary = async () => {
    setSummaryLoading(true);
    try {
      const data = await getUnitSummary(token);
      setSummary(data);
      setSummaryError('');
    } catch (error) {
      setSummaryError(error.message || 'Failed to load summary');
    } finally {
      setSummaryLoading(false);
    }
  };

  const loadContributions = async (page = 1) => {
    setContribLoading(true);
    setContribPage(page);
    try {
      const data = await getUnitContributions(token, { page });
      setContribData(data);
      setContribError('');
    } catch (error) {
      setContribError(error.message || 'Failed to load contributions');
    } finally {
      setContribLoading(false);
    }
  };

  const loadContributionHistory = async (page = 1, perPage = 100) => {
    setHistoryLoading(true);
    setHistoryPage(page);
    try {
      const data = await getContributionHistory(token, { page, perPage });
      setHistoryData(data);
      setHistoryError('');
    } catch (error) {
      setHistoryError(error.message || 'Failed to load contribution history');
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role_id === 5) {
      loadSummary();
      loadContributions(1);
      loadContributionHistory(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user?.role_id]);

  const handleContributionSubmit = async (payload) => {
    await createContribution(token, payload);
    await Promise.all([
      loadSummary(),
      loadContributions(contribPage),
      loadContributionHistory(historyPage),
    ]);
  };

  const handleContributionUpdate = async (payload) => {
    await updateContribution(token, payload);
    await Promise.all([
      loadSummary(),
      loadContributions(contribPage),
      loadContributionHistory(historyPage),
    ]);
  };

  const handleContributionDelete = async (id) => {
    await deleteContribution(token, id);
    await Promise.all([
      loadSummary(),
      loadContributions(contribPage),
      loadContributionHistory(historyPage),
    ]);
  };

  const totals = summary?.contributions ?? {
    total_collected: 0,
  };

  return (
    <div className="landing-container">
      <header className="landing-header">
        <div>
          <h1 className="landing-title">Welcome, {user?.orgname || user?.login_name || 'Unit Admin'}</h1>
          <p className="landing-subtitle">Track your unit&apos;s members and sadaqa contributions.</p>
        </div>
        <button type="button" className="landing-logout" onClick={onLogout}>
          Log out
        </button>
      </header>

      <nav className="landing-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          className={activeTab === 'dashboard' ? 'landing-tab active' : 'landing-tab'}
          onClick={() => setActiveTab('dashboard')}
        >
          Dashboard
        </button>
        <button
          type="button"
          role="tab"
          className={activeTab === 'contributions' ? 'landing-tab active' : 'landing-tab'}
          onClick={() => setActiveTab('contributions')}
          aria-label="contributions"
        >
          Sadaqa contributions
        </button>
      </nav>

      <section className="landing-content">
        {activeTab === 'dashboard' ? (
          <DashboardSummary
            data={summary}
            loading={summaryLoading}
            error={summaryError}
            onRetry={loadSummary}
          />
        ) : (
          <ContributionsTab
            data={contribData}
            loading={contribLoading}
            error={contribError}
            totals={totals}
            currentPage={contribPage}
            onRetry={() => loadContributions(contribPage)}
            onPageChange={loadContributions}
            onSubmit={handleContributionSubmit}
            history={historyData}
            historyLoading={historyLoading}
            historyError={historyError}
            historyPage={historyPage}
            onHistoryRetry={() => loadContributionHistory(historyPage)}
            onHistoryPageChange={loadContributionHistory}
            onUpdateContribution={handleContributionUpdate}
            onDeleteContribution={handleContributionDelete}
          />
        )}
      </section>
    </div>
  );
}
